import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Mail, Phone, MessageCircle, CreditCard,
  Loader2, Eye, Trash2, ChevronLeft, Clock, CheckCircle,
  AlertTriangle, Send, StickyNote, DollarSign, Percent,
  RefreshCw, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { honorariosAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ESTADOS = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  en_seguimiento: { label: 'En seguimiento', color: 'bg-blue-100 text-blue-800', icon: AlertTriangle },
  pagado: { label: 'Pagado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelado: { label: 'Cancelado', color: 'bg-gray-100 text-gray-800', icon: AlertTriangle },
};

const METODOS_PAGO = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia bancaria' },
  { value: 'bizum', label: 'Bizum' },
  { value: 'tarjeta', label: 'Tarjeta de crédito' },
  { value: 'otro', label: 'Otro' },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ============================================================================
// BUSCADOR DE EXPEDIENTES
// ============================================================================
const ExpedienteSearch = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRef = React.useRef(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await expedientesAPI.getAll({ search: query, page_size: 15 });
        setResults(res.data.data || res.data || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
  }, [query]);

  return (
    <div className="space-y-2">
      <Label>Buscar expediente</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="honorarios-expediente-search"
          placeholder="Escribe n. expediente o nombre..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-10"
        />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
      </div>
      {results.length > 0 && (
        <div className="border rounded-md max-h-48 overflow-y-auto bg-white shadow-sm relative z-50">
          {results.map(exp => (
            <button
              key={exp.id}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 text-sm"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSelect(exp); setQuery(''); setResults([]); }}
            >
              <span className="font-medium text-blue-700">{exp.numero_expediente}</span>
              <span className="text-gray-500 ml-2">{exp.titulo}</span>
              {exp.cliente_nombre && <span className="text-gray-400 ml-2">({exp.cliente_nombre})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MODAL: CREAR HONORARIO
// ============================================================================
const CreateHonorarioDialog = ({ open, onClose, onCreated }) => {
  const [selectedExp, setSelectedExp] = useState(null);
  const [clienteData, setClienteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ importe_reclamado: '', porcentaje_honorarios: '', observaciones: '' });

  const handleSelectExpediente = async (exp) => {
    setSelectedExp(exp);
    try {
      const res = await honorariosAPI.getClienteExpediente(exp.id);
      setClienteData(res.data);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron obtener datos del cliente', variant: 'destructive' });
    }
  };

  const importeCalculado = form.importe_reclamado && form.porcentaje_honorarios
    ? (parseFloat(form.importe_reclamado) * parseFloat(form.porcentaje_honorarios) / 100).toFixed(2)
    : '0.00';

  const handleSubmit = async () => {
    if (!selectedExp || !form.importe_reclamado || !form.porcentaje_honorarios) {
      toast({ title: 'Error', description: 'Rellena todos los campos obligatorios', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await honorariosAPI.create({
        expediente_id: selectedExp.id,
        importe_reclamado: parseFloat(form.importe_reclamado),
        porcentaje_honorarios: parseFloat(form.porcentaje_honorarios),
        observaciones: form.observaciones || null,
      });
      toast({ title: 'Honorario creado correctamente' });
      onCreated();
      handleClose();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error al crear', variant: 'destructive' });
    }
    setLoading(false);
  };

  const handleClose = () => {
    setSelectedExp(null);
    setClienteData(null);
    setForm({ importe_reclamado: '', porcentaje_honorarios: '', observaciones: '' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="create-honorario-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo seguimiento de honorarios</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!selectedExp ? (
            <ExpedienteSearch onSelect={handleSelectExpediente} />
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-800">{selectedExp.numero_expediente}</p>
                    <p className="text-sm text-blue-600">{selectedExp.titulo}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedExp(null); setClienteData(null); }}>
                    Cambiar
                  </Button>
                </div>
              </div>

              {clienteData && (
                <div className="p-3 bg-gray-50 rounded-lg space-y-1">
                  <p className="text-sm font-medium text-gray-700">Datos del cliente</p>
                  <p className="text-sm">Nombre: <span className="font-medium">{clienteData.cliente_nombre || 'No disponible'}</span></p>
                  <p className="text-sm">Email: <span className="font-medium">{clienteData.cliente_email || 'No disponible'}</span></p>
                  <p className="text-sm">Tel: <span className="font-medium">{clienteData.cliente_telefono || 'No disponible'}</span></p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Importe reclamado *</Label>
                  <Input
                    data-testid="honorario-importe-reclamado"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.importe_reclamado}
                    onChange={(e) => setForm({ ...form, importe_reclamado: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Porcentaje honorarios (%) *</Label>
                  <Input
                    data-testid="honorario-porcentaje"
                    type="number"
                    step="0.01"
                    placeholder="10"
                    value={form.porcentaje_honorarios}
                    onChange={(e) => setForm({ ...form, porcentaje_honorarios: e.target.value })}
                  />
                </div>
              </div>

              <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                <p className="text-sm text-green-600">Importe honorarios calculado</p>
                <p className="text-2xl font-bold text-green-800">{formatCurrency(parseFloat(importeCalculado))}</p>
              </div>

              <div>
                <Label>Observaciones</Label>
                <Textarea
                  data-testid="honorario-observaciones"
                  placeholder="Notas adicionales..."
                  value={form.observaciones}
                  onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button
            data-testid="honorario-submit-btn"
            onClick={handleSubmit}
            disabled={!selectedExp || !form.importe_reclamado || !form.porcentaje_honorarios || loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MODAL: ENVIAR EMAIL
// ============================================================================
const SendEmailDialog = ({ open, onClose, honorario, onSent }) => {
  const [plantillas, setPlantillas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    plantilla_id: null,
    cuenta_email_id: null,
    asunto: '',
    cuerpo: '',
    destinatario_email: '',
  });

  useEffect(() => {
    if (!open || !honorario) return;
    const load = async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          honorariosAPI.getPlantillas(honorario.id),
          honorariosAPI.getCuentasEmail(),
        ]);
        setPlantillas(pRes.data || []);
        setCuentas(cRes.data || []);

        // Pre-fill
        setForm({
          plantilla_id: null,
          cuenta_email_id: cRes.data?.[0]?.id || null,
          asunto: `Reclamación honorarios - Expediente ${honorario.numero_expediente || ''}`,
          cuerpo: '',
          destinatario_email: honorario.cliente_email || '',
        });
      } catch {
        toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
      }
    };
    load();
  }, [open, honorario]);

  const handleSelectPlantilla = (plantillaId) => {
    const p = plantillas.find(pl => pl.id === parseInt(plantillaId));
    if (p) {
      // Replace variables in template
      let asunto = p.asunto || '';
      let cuerpo = p.cuerpo || '';
      const vars = {
        '{{cliente_nombre}}': honorario?.cliente_nombre || '',
        '{{expediente_numero}}': honorario?.numero_expediente || '',
        '{{importe_reclamado}}': honorario?.importe_reclamado?.toFixed(2) || '0.00',
        '{{porcentaje_honorarios}}': honorario?.porcentaje_honorarios?.toString() || '0',
        '{{importe_honorarios}}': honorario?.importe_honorarios?.toFixed(2) || '0.00',
      };
      Object.entries(vars).forEach(([k, v]) => {
        asunto = asunto.replace(new RegExp(k.replace(/[{}]/g, '\\$&'), 'g'), v);
        cuerpo = cuerpo.replace(new RegExp(k.replace(/[{}]/g, '\\$&'), 'g'), v);
      });
      setForm(prev => ({ ...prev, plantilla_id: parseInt(plantillaId), asunto, cuerpo }));
    }
  };

  const handleSend = async () => {
    if (!form.asunto || !form.cuerpo) {
      toast({ title: 'Error', description: 'Rellena asunto y cuerpo del email', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await honorariosAPI.enviarEmail(honorario.id, form);
      toast({ title: 'Email enviado correctamente' });
      onSent();
      onClose();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error al enviar email', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle>Enviar email de reclamación</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Plantilla</Label>
              <Select onValueChange={handleSelectPlantilla}>
                <SelectTrigger data-testid="email-plantilla-select">
                  <SelectValue placeholder="Seleccionar plantilla..." />
                </SelectTrigger>
                <SelectContent>
                  {plantillas.map(p => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cuenta de envío</Label>
              <Select value={form.cuenta_email_id ? String(form.cuenta_email_id) : undefined} onValueChange={(v) => setForm({ ...form, cuenta_email_id: parseInt(v) })}>
                <SelectTrigger data-testid="email-cuenta-select">
                  <SelectValue placeholder="Seleccionar cuenta..." />
                </SelectTrigger>
                <SelectContent>
                  {cuentas.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.nombre} ({c.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Destinatario</Label>
            <Input
              data-testid="email-destinatario"
              value={form.destinatario_email}
              onChange={(e) => setForm({ ...form, destinatario_email: e.target.value })}
              placeholder="email@cliente.com"
            />
          </div>

          <div>
            <Label>Asunto</Label>
            <Input
              data-testid="email-asunto"
              value={form.asunto}
              onChange={(e) => setForm({ ...form, asunto: e.target.value })}
            />
          </div>

          <div>
            <Label>Cuerpo del email</Label>
            <Textarea
              data-testid="email-cuerpo"
              value={form.cuerpo}
              onChange={(e) => setForm({ ...form, cuerpo: e.target.value })}
              rows={8}
              placeholder="Escriba el contenido del email..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Variables disponibles: {'{{cliente_nombre}}'}, {'{{expediente_numero}}'}, {'{{importe_reclamado}}'}, {'{{porcentaje_honorarios}}'}, {'{{importe_honorarios}}'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="email-send-btn" onClick={handleSend} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Enviar email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MODAL: REGISTRAR ACCIÓN (Llamada/WhatsApp/Nota)
// ============================================================================
const RegisterActionDialog = ({ open, onClose, honorario, actionType, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ descripcion: '', resultado: '' });

  const labels = {
    llamada: { title: 'Registrar llamada telefónica', icon: Phone },
    whatsapp: { title: 'Registrar WhatsApp', icon: MessageCircle },
    nota: { title: 'Añadir nota', icon: StickyNote },
  };

  const config = labels[actionType] || labels.nota;
  const Icon = config.icon;

  const handleSubmit = async () => {
    if (!form.descripcion) {
      toast({ title: 'Error', description: 'Describe la acción realizada', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await honorariosAPI.createAccion(honorario.id, {
        tipo: actionType,
        descripcion: form.descripcion,
        resultado: form.resultado || null,
      });
      toast({ title: 'Acción registrada' });
      onDone();
      onClose();
      setForm({ descripcion: '', resultado: '' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); setForm({ descripcion: '', resultado: '' }); }}>
      <DialogContent data-testid="register-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" /> {config.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Descripción *</Label>
            <Textarea
              data-testid="action-descripcion"
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder={actionType === 'llamada' ? 'Detalle de la conversación...' : actionType === 'whatsapp' ? 'Contenido del mensaje...' : 'Nota...'}
              rows={3}
            />
          </div>
          <div>
            <Label>Resultado</Label>
            <Input
              data-testid="action-resultado"
              value={form.resultado}
              onChange={(e) => setForm({ ...form, resultado: e.target.value })}
              placeholder="Ej: Cliente dice que pagará la semana que viene"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); setForm({ descripcion: '', resultado: '' }); }}>Cancelar</Button>
          <Button data-testid="action-submit-btn" onClick={handleSubmit} disabled={loading || !form.descripcion}>
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MODAL: REGISTRAR PAGO
// ============================================================================
const RegisterPaymentDialog = ({ open, onClose, honorario, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    fecha_pago: new Date().toISOString().split('T')[0],
    metodo_pago: '',
    referencia_pago: '',
    observaciones: '',
  });

  const handleSubmit = async () => {
    if (!form.metodo_pago) {
      toast({ title: 'Error', description: 'Selecciona el método de pago', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await honorariosAPI.registrarPago(honorario.id, {
        fecha_pago: form.fecha_pago ? new Date(form.fecha_pago).toISOString() : null,
        metodo_pago: form.metodo_pago,
        referencia_pago: form.referencia_pago || null,
        observaciones: form.observaciones || null,
      });
      toast({ title: 'Pago registrado', description: 'Se ha creado la actuación y el registro de pago en el expediente' });
      onDone();
      onClose();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error al registrar pago', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="register-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" /> Registrar pago de honorarios
          </DialogTitle>
        </DialogHeader>
        {honorario && (
          <div className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
              <p className="text-sm text-green-600">Importe a cobrar</p>
              <p className="text-2xl font-bold text-green-800">{formatCurrency(honorario.importe_honorarios)}</p>
              <p className="text-xs text-green-600">({honorario.porcentaje_honorarios}% de {formatCurrency(honorario.importe_reclamado)})</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha de pago *</Label>
                <Input
                  data-testid="pago-fecha"
                  type="date"
                  value={form.fecha_pago}
                  onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })}
                />
              </div>
              <div>
                <Label>Método de pago *</Label>
                <Select value={form.metodo_pago} onValueChange={(v) => setForm({ ...form, metodo_pago: v })}>
                  <SelectTrigger data-testid="pago-metodo-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {METODOS_PAGO.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Referencia de pago</Label>
              <Input
                data-testid="pago-referencia"
                value={form.referencia_pago}
                onChange={(e) => setForm({ ...form, referencia_pago: e.target.value })}
                placeholder="N. transferencia, recibo..."
              />
            </div>

            <div>
              <Label>Observaciones</Label>
              <Textarea
                data-testid="pago-observaciones"
                value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                placeholder="Notas adicionales..."
                rows={2}
              />
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
              Al registrar el pago se creará automáticamente:
              <ul className="list-disc ml-4 mt-1">
                <li>Una actuación en el expediente</li>
                <li>Un registro de pago en "Otras cantidades"</li>
              </ul>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="pago-submit-btn" onClick={handleSubmit} disabled={loading || !form.metodo_pago}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Confirmar pago
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ============================================================================
// VISTA DETALLE
// ============================================================================
const HonorarioDetailView = ({ honorario, onBack, onRefresh }) => {
  const [emailOpen, setEmailOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [actionType, setActionType] = useState('nota');
  const [paymentOpen, setPaymentOpen] = useState(false);

  const estado = ESTADOS[honorario.estado] || ESTADOS.pendiente;
  const EstadoIcon = estado.icon;

  const openAction = (type) => { setActionType(type); setActionOpen(true); };

  const accionIcons = {
    email: { icon: Mail, color: 'text-blue-600 bg-blue-100' },
    llamada: { icon: Phone, color: 'text-green-600 bg-green-100' },
    whatsapp: { icon: MessageCircle, color: 'text-emerald-600 bg-emerald-100' },
    nota: { icon: StickyNote, color: 'text-amber-600 bg-amber-100' },
  };

  return (
    <div className="space-y-4" data-testid="honorario-detail-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="honorario-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">{honorario.cliente_nombre || 'Sin cliente'}</h3>
          <p className="text-sm text-gray-500">Expediente: {honorario.numero_expediente}</p>
        </div>
        <Badge className={estado.color}>
          <EstadoIcon className="w-3 h-3 mr-1" />
          {estado.label}
        </Badge>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-xs text-gray-500 uppercase">Reclamado</p>
            <p className="text-xl font-bold text-gray-800">{formatCurrency(honorario.importe_reclamado)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-xs text-gray-500 uppercase">Porcentaje</p>
            <p className="text-xl font-bold text-gray-800">{honorario.porcentaje_honorarios}%</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4 text-center">
            <p className="text-xs text-green-600 uppercase">Honorarios</p>
            <p className="text-xl font-bold text-green-800">{formatCurrency(honorario.importe_honorarios)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Client info */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Email:</span>
              <span className="ml-2 font-medium">{honorario.cliente_email || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Teléfono:</span>
              <span className="ml-2 font-medium">{honorario.cliente_telefono || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Creado:</span>
              <span className="ml-2 font-medium">{formatDate(honorario.created_at)}</span>
            </div>
          </div>
          {honorario.fecha_pago && (
            <div className="mt-2 text-sm">
              <span className="text-gray-500">Fecha pago:</span>
              <span className="ml-2 font-medium text-green-700">{formatDate(honorario.fecha_pago)}</span>
              {honorario.metodo_pago_display && (
                <span className="ml-3 text-gray-500">Método: <span className="font-medium">{honorario.metodo_pago_display}</span></span>
              )}
              {honorario.referencia_pago && (
                <span className="ml-3 text-gray-500">Ref: <span className="font-medium">{honorario.referencia_pago}</span></span>
              )}
            </div>
          )}
          {honorario.observaciones && (
            <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">{honorario.observaciones}</div>
          )}
        </CardContent>
      </Card>

      {/* Action buttons */}
      {honorario.estado !== 'pagado' && (
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={() => setEmailOpen(true)} data-testid="btn-send-email">
            <Mail className="w-4 h-4 mr-1" /> Enviar email
          </Button>
          <Button size="sm" variant="outline" onClick={() => openAction('llamada')} data-testid="btn-register-call">
            <Phone className="w-4 h-4 mr-1" /> Registrar llamada
          </Button>
          <Button size="sm" variant="outline" onClick={() => openAction('whatsapp')} data-testid="btn-register-whatsapp">
            <MessageCircle className="w-4 h-4 mr-1" /> Registrar WhatsApp
          </Button>
          <Button size="sm" variant="outline" onClick={() => openAction('nota')} data-testid="btn-add-note">
            <StickyNote className="w-4 h-4 mr-1" /> Nota
          </Button>
          <Button size="sm" className="bg-green-600 hover:bg-green-700 ml-auto" onClick={() => setPaymentOpen(true)} data-testid="btn-register-payment">
            <CreditCard className="w-4 h-4 mr-1" /> Registrar pago
          </Button>
        </div>
      )}

      {/* Timeline */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Historial de acciones</CardTitle>
        </CardHeader>
        <CardContent>
          {(!honorario.acciones || honorario.acciones.length === 0) ? (
            <p className="text-sm text-gray-400 text-center py-4">No hay acciones registradas</p>
          ) : (
            <div className="space-y-3">
              {honorario.acciones.map(a => {
                const ai = accionIcons[a.tipo] || accionIcons.nota;
                const AIcon = ai.icon;
                return (
                  <div key={a.id} className="flex gap-3 items-start">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ai.color}`}>
                      <AIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{a.descripcion}</p>
                      {a.resultado && <p className="text-xs text-gray-500">{a.resultado}</p>}
                      {a.email_destinatario && <p className="text-xs text-gray-400">Enviado a: {a.email_destinatario}</p>}
                      <p className="text-xs text-gray-400 mt-0.5">{formatDateTime(a.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <SendEmailDialog open={emailOpen} onClose={() => setEmailOpen(false)} honorario={honorario} onSent={onRefresh} />
      <RegisterActionDialog open={actionOpen} onClose={() => setActionOpen(false)} honorario={honorario} actionType={actionType} onDone={onRefresh} />
      <RegisterPaymentDialog open={paymentOpen} onClose={() => setPaymentOpen(false)} honorario={honorario} onDone={onRefresh} />
    </div>
  );
};


// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const HonorariosTab = () => {
  const [honorarios, setHonorarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedHonorario, setSelectedHonorario] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [stats, setStats] = useState(null);
  const [descargando, setDescargando] = useState(false);

  const loadHonorarios = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado !== 'todos') params.estado = filtroEstado;
      if (search.trim()) params.search = search.trim();
      const res = await honorariosAPI.getAll(params);
      setHonorarios(res.data.honorarios || []);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los honorarios', variant: 'destructive' });
    }
    setLoading(false);
  }, [filtroEstado, search]);

  const loadStats = useCallback(async () => {
    try {
      const res = await honorariosAPI.getEstadisticas();
      setStats(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadHonorarios(); loadStats(); }, [loadHonorarios, loadStats]);

  const openDetail = async (h) => {
    try {
      const res = await honorariosAPI.get(h.id);
      setDetailData(res.data);
      setSelectedHonorario(h.id);
    } catch {
      toast({ title: 'Error', description: 'No se pudo cargar el detalle', variant: 'destructive' });
    }
  };

  const refreshDetail = async () => {
    if (selectedHonorario) {
      try {
        const res = await honorariosAPI.get(selectedHonorario);
        setDetailData(res.data);
      } catch { /* silent */ }
    }
    loadHonorarios();
    loadStats();
  };

  const handleDelete = async (h) => {
    if (!window.confirm('¿Eliminar este registro de honorarios?')) return;
    try {
      await honorariosAPI.delete(h.id);
      toast({ title: 'Honorario eliminado' });
      loadHonorarios();
      loadStats();
    } catch {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  // [v139] descarga del listado que se ve en pantalla
  const descargarListado = async (formato) => {
    if (!formato) return;
    setDescargando(true);
    try {
      const params = { formato };
      if (filtroEstado !== 'todos') params.estado = filtroEstado;
      if (search.trim()) params.search = search.trim();
      const res = await honorariosAPI.exportar(params);
      const cabecera = res.headers?.['content-disposition'] || '';
      const encontrado = /filename="?([^";]+)"?/.exec(cabecera);
      const nombre = encontrado ? encontrado[1]
        : `honorarios_${new Date().toISOString().slice(0, 10)}.${formato}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: `Descargado ${nombre}` });
    } catch {
      toast({ title: 'Error', description: 'No se pudo generar el archivo', variant: 'destructive' });
    }
    setDescargando(false);
  };

  // Detail view
  if (detailData && selectedHonorario) {
    return (
      <HonorarioDetailView
        honorario={detailData}
        onBack={() => { setSelectedHonorario(null); setDetailData(null); loadHonorarios(); loadStats(); }}
        onRefresh={refreshDetail}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="honorarios-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-gray-500 uppercase">Total registros</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-yellow-600 uppercase">Pendiente cobro</p>
              <p className="text-2xl font-bold text-yellow-700">{formatCurrency(stats.total_pendiente)}</p>
              <p className="text-xs text-gray-400">{stats.pendientes + stats.en_seguimiento} registros</p>
            </CardContent>
          </Card>
          <Card className="border-green-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-green-600 uppercase">Cobrado</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.total_cobrado)}</p>
              <p className="text-xs text-gray-400">{stats.pagados} registros</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-gray-500 uppercase">Total reclamado</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.total_reclamado)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="honorarios-search"
            placeholder="Buscar por cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
          <SelectTrigger className="w-[180px]" data-testid="honorarios-estado-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="pendiente">Pendiente</SelectItem>
            <SelectItem value="en_seguimiento">En seguimiento</SelectItem>
            <SelectItem value="pagado">Pagado</SelectItem>
            <SelectItem value="cancelado">Cancelado</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => { loadHonorarios(); loadStats(); }} data-testid="honorarios-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
        {/* [v139] descarga del listado que se ve en pantalla */}
        <Select value="" onValueChange={descargarListado} disabled={descargando || honorarios.length === 0}>
          <SelectTrigger className="w-[190px]" data-testid="honorarios-descargar-trigger">
            <span className="flex items-center gap-2 text-sm">
              {descargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {descargando ? 'Generando…' : 'Descargar listado'}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="xlsx" data-testid="honorarios-descargar-xlsx">Excel (.xlsx)</SelectItem>
            <SelectItem value="pdf" data-testid="honorarios-descargar-pdf">PDF (.pdf)</SelectItem>
            <SelectItem value="docx" data-testid="honorarios-descargar-docx">Word (.docx)</SelectItem>
            <SelectItem value="md" data-testid="honorarios-descargar-md">Markdown (.md)</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setCreateOpen(true)} data-testid="honorarios-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo honorario
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : honorarios.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No hay registros de honorarios</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-1" /> Crear primero
              </Button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/80">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Expediente</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Cliente</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Reclamado</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">%</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Honorarios</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">Estado</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">Acciones</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">Fecha</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600"></th>
                </tr>
              </thead>
              <tbody>
                {honorarios.map(h => {
                  const est = ESTADOS[h.estado] || ESTADOS.pendiente;
                  return (
                    <tr key={h.id} className="border-b hover:bg-gray-50/50 cursor-pointer" onClick={() => openDetail(h)}>
                      <td className="px-3 py-2 font-medium text-blue-700">{h.numero_expediente}</td>
                      <td className="px-3 py-2">{h.cliente_nombre || '-'}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(h.importe_reclamado)}</td>
                      <td className="px-3 py-2 text-center">{h.porcentaje_honorarios}%</td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(h.importe_honorarios)}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={`text-xs ${est.color}`}>{est.label}</Badge>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {h.num_emails > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-blue-600 text-xs mr-1">
                            <Mail className="w-3 h-3" />{h.num_emails}
                          </span>
                        )}
                        {h.num_acciones > h.num_emails && (
                          <span className="text-gray-400 text-xs">+{h.num_acciones - h.num_emails}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-gray-500">
                        {h.fecha_pago ? formatDate(h.fecha_pago) : formatDate(h.created_at)}
                      </td>
                      <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openDetail(h)} data-testid={`honorario-view-${h.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {h.estado !== 'pagado' && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(h)} data-testid={`honorario-delete-${h.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <CreateHonorarioDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { loadHonorarios(); loadStats(); }} />
    </div>
  );
};

export default HonorariosTab;
